import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "../../components/ui-components/Toast";
import { getAutomation, setAutomation } from "./invoiceReviewApi";

// One row per configurable rule (amount>0 is always-on and not shown).
const RULES = [
  { key: "vendor_recognized", label: "Recognized vendor", help: "Only auto-process vendors you've confirmed before.", risky: true },
  { key: "invoice_number", label: "Invoice number present", help: "Require an invoice number." },
  { key: "invoice_date", label: "Valid invoice date", help: "Require a valid invoice date." },
  { key: "billing_period", label: "Billing period present", help: "Require a billing period on the invoice." },
  { key: "currency", label: "Valid currency", help: "Require a 3-letter ISO currency code." },
  { key: "duplicate", label: "No possible duplicate", help: "Route possible duplicates to review.", risky: true },
  { key: "confidence", label: "Confidence floor", help: "Require extraction confidence at or above the threshold." },
];
const DEFAULTS = {
  vendor_recognized: true, invoice_number: true, invoice_date: true, billing_period: false,
  currency: true, duplicate: true, confidence: true, confidence_threshold: 0.9, amount_ceiling_cents: 0,
};

const InvoiceAutomationSettingsPage = () => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data } = useQuery(["invoice-automation"], getAutomation);
  const [enabled, setEnabled] = useState(true);
  const [rules, setRules] = useState(DEFAULTS);
  const [pctText, setPctText] = useState("90");
  const [ceilingText, setCeilingText] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    // Skip once the user has started editing so a background refetch of the
    // server config can't clobber in-progress, unsaved changes.
    if (!data || dirty) return;
    setEnabled(data.enabled !== false);
    const r = { ...DEFAULTS, ...(data.rules || {}) };
    setRules(r);
    setPctText(String(Math.round((r.confidence_threshold ?? 0.9) * 100)));
    setCeilingText(r.amount_ceiling_cents ? String(r.amount_ceiling_cents / 100) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const touch = (fn) => { fn(); setDirty(true); };
  const toggleRule = (key) => touch(() => setRules((r) => ({ ...r, [key]: !r[key] })));

  const saveMut = useMutation(
    () => {
      const pct = Number(pctText);
      const ceil = ceilingText.trim() === "" ? 0 : Math.round(Number(ceilingText) * 100);
      return setAutomation({
        enabled,
        rules: { ...rules, confidence_threshold: pct / 100, amount_ceiling_cents: ceil },
      });
    },
    { onSuccess: () => { toast.success("Automation settings saved"); qc.invalidateQueries(["invoice-automation"]); setDirty(false); navigate("/invoice-review"); },
      onError: () => toast.error("Couldn't save. Please try again.") }
  );

  const pct = Number(pctText);
  const ceil = ceilingText.trim() === "" ? 0 : Number(ceilingText);
  // Only validate the confidence % when its rule is enabled. Guard the empty case
  // explicitly: Number("") is 0 (not NaN), which would otherwise silently save a 0%
  // floor and stop the confidence gate from ever blocking.
  const pctInvalid = rules.confidence && (pctText.trim() === "" || Number.isNaN(pct) || pct < 0 || pct > 100);
  const ceilInvalid = ceilingText.trim() !== "" && (Number.isNaN(ceil) || ceil < 0);
  const riskyOff = RULES.some((r) => r.risky && !rules[r.key]);

  return (
    <div className="text-left flex flex-auto flex-col ml-10 mt-6 mr-6 pb-24">
      <button type="button" onClick={() => navigate("/invoice-review")} className="text-sm text-primary hover:underline self-start">← Back to Queue</button>
      <h1 className="text-lg font-semibold text-text mt-2">Automation Settings</h1>
      <p className="text-sm text-text-2 max-w-2xl mt-1 mb-5">
        Choose which checks must pass before an invoice is processed automatically. Everything else is routed to review.
      </p>

      <div className="rounded-card border border-card-bd bg-card p-4 mb-4 flex items-center justify-between" style={{ maxWidth: 720 }}>
        <div><div className="text-sm font-medium text-text">Automation</div>
          <div className="text-xs text-text-2">Master switch. When off, every invoice goes to review.</div></div>
        <button type="button" aria-label="Toggle automation" onClick={() => touch(() => setEnabled((v) => !v))}
          className={`px-3 py-1 rounded-ctrl text-sm border ${enabled ? "bg-success-soft text-success border-success/30" : "bg-surface-2 text-text-3 border-card-bd"}`}>
          {enabled ? "On" : "Off"}
        </button>
      </div>

      <div className="rounded-card border border-card-bd bg-card divide-y divide-card-bd" style={{ maxWidth: 720 }}>
        {RULES.map((rule) => (
          <div key={rule.key} className="flex items-start justify-between gap-4 p-4">
            <div className="min-w-0">
              <div className="text-sm font-medium text-text">{rule.label}</div>
              <div className="text-xs text-text-2">{rule.help}</div>
              {rule.key === "confidence" && rules.confidence && (
                <div className="mt-2 flex items-center gap-2">
                  <input type="number" min="0" max="100" aria-label="Minimum confidence percent" value={pctText} onChange={(e) => touch(() => setPctText(e.target.value))}
                    style={{ width: 72 }} className={`border rounded-ctrl px-2 py-1 text-sm ${pctInvalid ? "border-danger" : "border-card-bd"}`} />
                  <span className="text-xs text-text-2">% minimum confidence</span>
                </div>
              )}
            </div>
            <button type="button" aria-label={`Toggle ${rule.label}`} onClick={() => toggleRule(rule.key)}
              className={`shrink-0 px-3 py-1 rounded-ctrl text-sm border ${rules[rule.key] ? "bg-success-soft text-success border-success/30" : "bg-surface-2 text-text-3 border-card-bd"}`}>
              {rules[rule.key] ? "On" : "Off"}
            </button>
          </div>
        ))}
        <div className="flex items-start justify-between gap-4 p-4">
          <div className="min-w-0">
            <div className="text-sm font-medium text-text">Amount ceiling</div>
            <div className="text-xs text-text-2">Auto-process only invoices at or under this amount; larger ones always go to review. Blank = no limit.</div>
          </div>
          <div className="shrink-0 flex items-center gap-1">
            <span className="text-sm text-text-2">€</span>
            <input type="number" min="0" step="0.01" aria-label="Amount ceiling euros" value={ceilingText} placeholder="No limit"
              onChange={(e) => touch(() => setCeilingText(e.target.value))}
              style={{ width: 110 }} className={`border rounded-ctrl px-2 py-1 text-sm text-right ${ceilInvalid ? "border-danger" : "border-card-bd"}`} />
          </div>
        </div>
      </div>

      {riskyOff && (
        <div className="mt-3 rounded-card px-3 py-2 text-sm bg-warning-soft text-warning" style={{ maxWidth: 720 }}>
          Heads up: turning off Recognized vendor or No possible duplicate lets unrecognized-vendor or possibly-duplicate invoices post automatically.
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 border-t border-card-bd bg-card px-8 py-3 flex items-center justify-end gap-2">
        <button type="button" onClick={() => navigate("/invoice-review")} className="px-4 py-1.5 text-sm rounded-ctrl border border-card-bd">Cancel</button>
        <button type="button" onClick={() => saveMut.mutate()} disabled={!dirty || pctInvalid || ceilInvalid || saveMut.isLoading}
          className="px-4 py-1.5 text-sm rounded-ctrl bg-primary text-white disabled:opacity-50">{saveMut.isLoading ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
};
export default InvoiceAutomationSettingsPage;
